import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { LuAlertTriangle } from 'react-icons/lu';
import { Alert, Button, Modal } from 'components/common';
import { INPUT_CLS } from 'helpers/formStyles';
import { CONFIRMATION_PHRASE } from './api';

// Second gate. The first was the dry run behind `preview`: the server already
// told us exactly which of the selected rows it would remove and which it would
// refuse because they are no longer orphaned. Nothing here is a guess.
const PurgeConfirmModal = ({ show, preview, submitting, onHide, onConfirm }) => {
  const [phrase, setPhrase] = useState('');

  // A fresh confirmation for every attempt — never carry a typed phrase over
  // from the previous selection.
  useEffect(() => { if (show) setPhrase(''); }, [show]);

  const deletable = preview?.deleted_ids || [];
  const skipped   = preview?.skipped_ids || [];
  const armed     = phrase.trim() === CONFIRMATION_PHRASE && deletable.length > 0;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      title="Remove orphaned attachments"
      footer={(
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
          <Button variant="secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
          <Button variant="danger" onClick={onConfirm} disabled={!armed} loading={submitting}>
            Remove {deletable.length} {deletable.length === 1 ? 'row' : 'rows'}
          </Button>
        </div>
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-3">
        <Alert
          type="warning"
          title={`${deletable.length} attachment ${deletable.length === 1 ? 'row' : 'rows'} will be deleted`}
          message="This removes the database rows only. The files are already gone — that is what makes these rows orphans — so nothing downloadable is lost. The deletion cannot be undone."
        />

        {skipped.length > 0 && (
          <Alert
            type="info"
            title={`${skipped.length} selected ${skipped.length === 1 ? 'row is' : 'rows are'} no longer orphaned and will be kept`}
            message={`The server re-checked every row just now and refused these: ${skipped.join(', ')}. Their file came back, or the id belongs to a healthy attachment. Reload the list to see the current state.`}
          />
        )}

        {deletable.length > 0 && (
          <div>
            <p className="tw-text-[12.5px] tw-text-slate-500 tw-mb-1">Attachment ids to remove</p>
            <div className="tw-max-h-32 tw-overflow-y-auto tw-rounded tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-2 tw-text-[12px] tw-text-slate-700 tw-font-mono tw-break-all">
              {deletable.join(', ')}
            </div>
          </div>
        )}

        <div>
          <label htmlFor="orphan-purge-confirm" className="tw-block tw-text-[12.5px] tw-font-medium tw-text-slate-700 tw-mb-1">
            Type <span className="tw-font-mono tw-font-semibold">{CONFIRMATION_PHRASE}</span> to confirm
          </label>
          <input
            id="orphan-purge-confirm"
            type="text"
            value={phrase}
            autoComplete="off"
            onChange={(e) => setPhrase(e.target.value)}
            placeholder={CONFIRMATION_PHRASE}
            className={INPUT_CLS}
          />
          {!armed && deletable.length > 0 && (
            <p className="tw-flex tw-items-center tw-gap-1.5 tw-text-[12px] tw-text-slate-500 tw-mt-1.5 tw-mb-0">
              <LuAlertTriangle size={12} /> The remove button stays disabled until the phrase matches exactly.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

PurgeConfirmModal.propTypes = {
  show:       PropTypes.bool.isRequired,
  preview:    PropTypes.shape({
    deleted_ids: PropTypes.arrayOf(PropTypes.number),
    skipped_ids: PropTypes.arrayOf(PropTypes.number),
  }),
  submitting: PropTypes.bool,
  onHide:     PropTypes.func.isRequired,
  onConfirm:  PropTypes.func.isRequired,
};

export default PurgeConfirmModal;
